import * as moment from 'moment';
import * as _ from "lodash";
import { Point } from '../Point'
import { Rectangle } from '../Rectangle'
import { Id_floor, Id_workorder, Id_location } from '../api/Identifiers'

import { Map, fromJS } from 'immutable'
import { Translate } from '../Geometry';
import { exists } from '../utils';

interface WorkorderJSON {
    active: boolean;
    adj_coords: number[];
    category: string;
    cmsViewIcon: string;   //  AWH remove?
    coords: number[];
    icon: string;
    icon_red: string;
    icon_yellow: string;
    is_red: boolean;
    is_yellow: boolean;
    id: string;   // id_workorder is a UUID
    id_floor: number;
    id_location: number;
    idCmsStatus: number;
    idFloor: number;   // AWH remove?
    rawCoords: number[];
    seconds_until_due: number;
    type: string;
    xCoord: number;  // AWH remove?
    yCoord: number;  // AWH remove?
    has_coords: boolean;
    is_multi_asset: boolean;
    asset_info: any[];
}

export class Workorder {
    private _active: boolean;
    private _category: string;
    private _adj_coords: Point;
    private _adj_geo_ref: Rectangle;
    private _raw_coords: Point;
    private _raw_geo_ref: Rectangle;

    private _icon: string;
    private _id: Id_workorder;
    private id_cms_status: number;
    private _id_location: Id_location;
    private _id_floor: Id_floor;
    private _raw: any;
    private status: string;
    private _type: string;
    private _has_coords: boolean = true;
    private _is_multi_asset: boolean;
    private _asset_info: any[];

    get active(): boolean { return this._active; }
    get category(): string { return this._category; }
    get id_floor(): Id_floor {
        if (!this._id_floor) {
            return Id_floor.wrap(this._id_location.value)
        }
        return this._id_floor;
    }
    get id_location(): Id_location { return this._id_location; }
    get id(): Id_workorder { return this._id; }
    get icon(): string { return this._icon; }
    get is_red(): boolean { return this.seconds_until_due < 0; }
    get is_yellow(): boolean { return this.seconds_until_due < 60 * 60 * 24 * 7; }  // due in less than 7 days

    get icon_red(): string {
        return this._icon.replace('/48/', '/48_red/');
    }

    get icon_yellow(): string {
        return this._icon.replace('/48/', '/48_yellow/');
    }

    public get raw(): any {
        return this._raw;
    }
    public get type(): string {
        return this._type;
    }
    public get coord(): Point {
        return this._adj_coords;
    }
    public get geoRef(): Rectangle {
        return this._adj_geo_ref;
    }

    get date_needed_by(): moment.Moment {
        if (!_.has(this.raw, 'dateNeededBy')) {
            return moment((moment().year() + 100) + "-01-01") // if no dateNeededBy is present, set the due date to 100 years in the future
        }
        return moment(this.raw.dateNeededBy);
    }

    get seconds_until_due(): number {
        return Math.ceil((moment.duration(this.date_needed_by.diff(moment())).asSeconds()))
    }


    public static build_from_raw(raw_workorder: any): Workorder {

        if (!raw_workorder.id) {
            console.warn("Error. Workorder with missing id")
            return null;
        }

        if (!raw_workorder.assets) {
            var raw = raw_workorder.id
            console.warn("Error. Workorder is missing assets - " + raw);
        }

        let r: Workorder = new Workorder

        r._raw = (<any>Object).assign({}, raw_workorder);
        r._id = Id_workorder.wrap(raw_workorder.id)
        r._active = utilsWorkorderIsActive(raw_workorder.idCmsStatus)
        if (!raw_workorder.assetCount){
            r._is_multi_asset = false;
        }
        else{
            r._is_multi_asset = utilsWorkorderIsMultiAsset(raw_workorder.assetCount);
        }
        if (r._is_multi_asset){
            r._asset_info = utilsWorkorderMultiAssetInfo(raw_workorder.assets);
        }
        r.id_cms_status = raw_workorder.idCmsStatus;
        r._id_floor = Id_floor.wrap(raw_workorder.idFloor)
        if (!r._id_floor) {
            console.warn(`malformed workorder ${r.id.toString()} has no idFloor value`)
            return null
        }

        r._id_location = Id_location.wrap(raw_workorder.idLocation)
        if (!r._id_location) {
            console.warn(`malformed workorder ${r.id.toString()} has no idLocation value`)
            return null
        }

        r.status = raw_workorder.status
        r._category = raw_workorder.category;
        if (!r.category) {
            console.warn(`malformed workorder ${r.id.toString()} has no category value`)
            return null;
        }
        r._type = utilsWorkorderType(r.category, raw_workorder.idCmsStatus)
        if (!raw_workorder.cmsViewIcon) {
            console.warn(`malformed workorder ${r.id.toString()} has no cmsViewIcon value`)
            return null
        }
        r._icon = utilsWorkorderIcon(raw_workorder.cmsViewIcon)
        r._raw_coords = Point.build(raw_workorder.xCoord, raw_workorder.yCoord)
        if (!r._raw_coords) {
            r._raw_coords = Point.build(5000, 5000);
            r._has_coords = false;
        }
        else {
            if (r._raw_coords.X === 0 && r._raw_coords.Y === 0) {
                r._has_coords = false;
            }
            if (r._raw_coords.X === 500 && r._raw_coords.Y === 500) {
                r._has_coords = false;
            }
            if (r._raw_coords.X === 5000 && r._raw_coords.Y === 5000) {
                r._has_coords = false;
            }
        }

        
        r._raw_geo_ref = Rectangle.build_from_numbers(raw_workorder.p1xCoord, raw_workorder.p1yCoord, raw_workorder.p2xCoord, raw_workorder.p2yCoord)

        r._adj_coords = r._raw_coords;
        r._adj_geo_ref = r._raw_geo_ref;
        return r;
    }

    public get has_coords(): boolean { return this._has_coords; }

    public get is_multi_asset(): boolean {return this._is_multi_asset; }

    public get asset_info(): any[]{return this._asset_info;}

    public toJSON(): WorkorderJSON {

        let coords_array: number[];
        let raw_geo_ref_json: any[];
        if (this._raw_coords === null) {
            //            console.error("coords are missing.")
            coords_array = [null, null]
        }
        else {
            coords_array = this._raw_coords.toArray();   // AWH try removing this.
        }
        if (this._raw_geo_ref === null) {
            //            console.error("Geo Reference is missing.")
            raw_geo_ref_json = [[null, null], [null, null]]
        }
        else {
            raw_geo_ref_json = this._raw_geo_ref.toArray();
        }

        let adj_geo_ref_json:any = raw_geo_ref_json;
        if (this._adj_geo_ref){
            adj_geo_ref_json = this._adj_geo_ref.toArray();
        }

        let adj_coords_json:any = coords_array;
        if (exists(this._adj_coords)){
            adj_coords_json = this._adj_coords.toArray();
        }

        const r: WorkorderJSON = {
            active: this.active,
            adj_coords: adj_coords_json,
            coords: coords_array,
            category: this.category,
            cmsViewIcon: this.icon,
            icon: this.icon,
            icon_red: this.icon_red,
            icon_yellow: this.icon_yellow,
            id: this.id.value,
            id_location: this.id_floor.value,
            id_floor: this.id_floor.value,
            idCmsStatus: this.id_cms_status,
            idFloor: this.id_floor.value,
            is_red: this.is_red,
            is_yellow: this.is_yellow,
            rawCoords: coords_array,
            seconds_until_due: this.seconds_until_due,
            type: this.type,
            xCoord: coords_array[0],
            yCoord: coords_array[1],
            has_coords: this.has_coords,
            is_multi_asset: this.is_multi_asset,
            asset_info: this._asset_info
        };

        return r
    }

    public translate(drawing_p1p2: Rectangle, p0: Point) {
        if (exists(drawing_p1p2)) {
            if (!exists(p0)) {
                p0 = Point.build(0, 0);
            }
            if (!this._has_coords) {
                // if this asset has no coords, set the adj coord to the provided p0
                this._adj_coords = p0;
                this._adj_geo_ref = drawing_p1p2;

            } else {
                this._adj_coords = Translate(this._raw_coords, this._raw_geo_ref, drawing_p1p2);
                this._adj_geo_ref = drawing_p1p2;
            }
        }
    }
}

export function utilsWorkorderIsActive(id_cms_status: number): boolean {

    /**
     IDCMSStatus   IDCMSModule  CMSStatus    Description
     1             2            Pending      New work order that requires review/or attention
     2             2            Active       Work order that has been assigned or is in progress
     4             2            Cancelled    Work order that was not completed (duplicate, not finished prior to next scheduled wo, etc)
     5             2            Complete     Work order that has been completed
     */

    if (id_cms_status == 1) // Pending
    {
        return true;
    }
    if (id_cms_status == 2) // Active
    {
        return true;
    }
    if (id_cms_status == 4) // Cancelled
    {
        return false;
    }
    if (id_cms_status == 5) // Complete
    {
        return false;
    }
    console.error("Unrecognized id_cms_status: " + id_cms_status)
    return false
}

export function utilsWorkorderIsMultiAsset(assetCount: number): boolean{
    if (assetCount > 1){
        return true;
    }
    else{
        return false;
    }
}


export function utilsWorkorderMultiAssetInfo(assets: any[]) :any[] {
    let asset_info: any[] =[];
    for(let i = 0; i<assets.length; i++){
        let id_asset = assets[i].id
        let id_workorder = assets[i].workOrderId
        let id_workorder_asset = assets[i].workOrderAssetId
        let raw_coords: Point = Point.build(assets[i].xCoord, assets[i].yCoord);
        if (!raw_coords){
            raw_coords = Point.build(0,0);
        }
        let raw_geo_ref: Rectangle= Rectangle.build_from_numbers(assets[i].p1xCoord, assets[i].p1yCoord, assets[i].p2xCoord, assets[i].p2yCoord);
        if (!raw_geo_ref){
            raw_geo_ref = Rectangle.build_from_numbers(0, 0, 0, 0);
        }
        asset_info.push({
            'coord':raw_coords.toArray(),
        'geo_ref':raw_geo_ref.toArray(), 
        'id_asset':id_asset,
        'id_workorder' :id_workorder,
        'id_workorder_asset':id_workorder_asset
    
    
    });

    }
    return asset_info;
}

export function utilsWorkorderType(category: string, id_cms_status: number):string {
    let category_reduced: string = category.toLowerCase().trim().replace(" ", "")

    let retVal: string = category_reduced + "_"

    if (utilsWorkorderIsActive(id_cms_status)) {
        retVal += "active"
    }
    else {
        retVal += "complete"
    }
    return retVal
}


export function utilsWorkorderIcon(cms_view_icon: string): string {
    if (cms_view_icon === undefined) {
        console.warn("null icon encountered.")
        cms_view_icon = ""
    }
    let icon: string = cms_view_icon

    if (icon.toLowerCase().substring(0, 4) !== "http") {
        console.warn("icon is not a complete URL")
        icon = "http://cmsview.azurewebsites.net/data/" + icon
    }

    return icon

}

/**
 * This takes in a raw_workorders object from the api, and returns a cleaned up list
 * of workorders
 * @param raw_workorders
 * @private
 */
export function cleanup_workorders_data(raw_workorder: any): any {  // This is an ECT (Extract, clean, transform) function - move it to a stand alone ECT js file
    let wo: Workorder = Workorder.build_from_raw(raw_workorder);
    let r: any = null
    if (wo !== null) {
        r = wo.toJSON();
    }
    return r;
}





// WEBPACK FOOTER //
// ./src/ts/workorders/Workorder.ts